import { useContext } from 'react'
import type { UseQueryOptions } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'

import GeoContext from 'contexts/geoContext'
import type { ExploreOfferParams } from 'tripPlanner/api/explore/offers'
import { getExploreOffers } from 'tripPlanner/api/explore/offers'
import type {
  GetExploreOffersResponse,
  GetExploreOffersError,
  ExploreOffersList,
} from 'tripPlanner/api/explore/types'

const MINUTES = 60 * 1000
const STALE_TIME = 5 * MINUTES

export type UseExploreOffersParams = {
  bounds?: string
  region?: string
  offerTypes?: string
  checkIn?: string
  checkOut?: string
}

function paramsHaveBounds(
  params: UseExploreOffersParams,
): params is UseExploreOffersParams & { bounds: string } {
  return Boolean(params.bounds)
}

function resolveExploreOfferRequestParams(
  params: UseExploreOffersParams & { bounds: string },
  region: string,
): ExploreOfferParams {
  const hasDates = Boolean(params.checkIn && params.checkOut)
  return {
    bounds: params.bounds,
    region,
    offerTypes: params.offerTypes,
    ...(hasDates ? { checkIn: params.checkIn, checkOut: params.checkOut } : {}),
  }
}

export function getExploreOffersQueryKey(
  params: UseExploreOffersParams,
  region: string,
) {
  return [
    'tripExplore',
    'hotels',
    params.bounds,
    region,
    params.offerTypes,
    params.checkIn,
    params.checkOut,
  ] as const
}

export const useExploreOffers = (
  params: UseExploreOffersParams,
  options?: UseQueryOptions<
    GetExploreOffersResponse['result'],
    GetExploreOffersError,
    ExploreOffersList
  >,
) => {
  const { currentRegionCode } = useContext(GeoContext)
  const region = params.region ?? currentRegionCode

  return useQuery({
    queryKey: getExploreOffersQueryKey(params, region),
    queryFn: () =>
      paramsHaveBounds(params)
        ? getExploreOffers(resolveExploreOfferRequestParams(params, region))
        : Promise.resolve({ offers: [], total: 0 }),
    staleTime: STALE_TIME,
    enabled: paramsHaveBounds(params),
    ...options,
  })
}
